import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import "./CollapsedDaysGuide.css";

// Une passe : bulle (0 – 0,3 s), tracé de la flèche (0,3 – 1,2 s), ondes à l’arrivée, effacement (2,5 – 3 s).
const PASS = 3000;
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const span = (t, start, end) => Math.min(1, Math.max(0, (t - start) / (end - start)));

/** Présente les journées repliées puis l'ensemble des choix de lecture, une fois à l'ouverture du relevé. */
export default function CollapsedDaysGuide({ target, controls, started, active, days }) {
  const [playing, setPlaying] = React.useState(false);
  // Journée hors de l'écran (détail long, petit écran) : la flèche attend qu'elle apparaisse.
  const [visible, setVisible] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const parts = React.useRef({});
  const steps = [
    { title: days > 1 ? "Les journées sont repliées" : "La journée est repliée",
      text: days > 1 ? "Cliquez sur une journée pour voir ses arrêts" : "Cliquez sur la journée pour voir ses arrêts" },
    { title: "Choisissez votre affichage", text: "Journées, par trajet ou par chauffeur : cliquez ici" },
  ];
  React.useEffect(() => {
    // Le chevron, cible de la flèche : la ligne de la journée peut dépasser l'écran sur un téléphone.
    const node = target.current?.querySelector("svg") || target.current;
    if (!active || !node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 1 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, target]);
  React.useEffect(() => {
    if (active && visible && !started.current) { started.current = true; setPlaying(true); }
    if (!active) setPlaying(false);
  }, [active, visible, started]);

  React.useLayoutEffect(() => {
    if (!playing) return undefined;
    const { bubble, trail, halo, head, arrow, gradient, rings } = parts.current;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let chevron = null;
    let frame = 0;
    const t0 = performance.now();
    const draw = (now) => {
      const elapsed = now - t0;
      const destination = step === 0 ? target.current?.querySelector("svg") : controls.current;
      if (!destination) { setPlaying(false); return; }
      if (elapsed >= PASS) {
        if (step === 0) setStep(1); else setPlaying(false);
        return;
      }
      chevron = step === 0 ? destination : null;
      const t = reduce ? Math.max(elapsed, 1200) : elapsed;
      // Positions relues à chaque image : la flèche suit sa cible si la page défile.
      const box = destination.getBoundingClientRect();
      const tx = box.left + box.width / 2, ty = box.top + box.height / 2;
      const size = bubble.getBoundingClientRect();
      const bx = Math.max(16, Math.min(tx + (step ? -480 : 480), window.innerWidth - size.width - 16));
      // Jamais au-dessus de la zone qui défile (filtres, en-tête de la page).
      const floor = (destination.closest("[data-guide-area]")?.getBoundingClientRect().top ?? 0) + 8;
      const by = Math.max(floor, ty - 210);
      bubble.style.left = `${bx}px`;
      bubble.style.top = `${by}px`;
      const sx = bx + 18, sy = by + size.height + 6, ex = tx + 9, ey = ty - 9;
      const d = `M ${sx} ${sy} C ${sx} ${sy + (ey - sy) * 0.55}, ${ex + (sx - ex) * 0.3} ${ey - (ey - sy) * 0.45}, ${ex} ${ey}`;
      trail.setAttribute("d", d);
      halo.setAttribute("d", d);
      gradient.setAttribute("x1", sx); gradient.setAttribute("y1", sy); gradient.setAttribute("x2", ex); gradient.setAttribute("y2", ey);
      const fade = 1 - span(t, 2500, 3000);
      const appear = ease(span(t, 0, 300));
      bubble.style.opacity = appear * fade;
      bubble.style.transform = `translateY(${(1 - appear) * -10}px) scale(${0.94 + 0.06 * appear})`;
      // La traînée se dessine et la pointe suit la tangente de la courbe.
      const length = trail.getTotalLength();
      const drawn = Math.max(0.01, length * ease(span(t, 300, 1200)));
      for (const path of [trail, halo]) { path.style.strokeDasharray = `${length}`; path.style.strokeDashoffset = `${length - drawn}`; }
      const tip = trail.getPointAtLength(drawn), back = trail.getPointAtLength(Math.max(0, drawn - 2));
      head.setAttribute("transform", `translate(${tip.x} ${tip.y}) rotate(${Math.atan2(tip.y - back.y, tip.x - back.x) * 180 / Math.PI})`);
      arrow.style.opacity = (t < 300 ? 0 : 1) * fade;
      // Arrivée : deux ondes autour de la cible et un petit mouvement du chevron de journée.
      rings.forEach((ring, index) => {
        const q = span(t, 1150 + index * 350, 1850 + index * 350);
        ring.style.left = `${tx}px`;
        ring.style.top = `${ty}px`;
        ring.style.width = `${step ? box.width + 8 : 26}px`;
        ring.style.height = `${step ? box.height + 8 : 26}px`;
        ring.style.marginLeft = `${-(step ? box.width + 8 : 26) / 2}px`;
        ring.style.marginTop = `${-(step ? box.height + 8 : 26) / 2}px`;
        ring.style.opacity = !reduce && q > 0 && q < 1 ? (1 - q) * 0.75 * fade : 0;
        ring.style.transform = `scale(${step ? 1 + q * 0.25 : 0.7 + q * 1.5})`;
      });
      if (chevron) {
        chevron.style.transform = reduce ? "" : `translateX(${Math.sin(span(t, 1150, 1650) * Math.PI * 2) * 3}px)`;
        chevron.style.color = t > 1150 && t < 2700 ? "var(--color-accent)" : "";
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      if (chevron) { chevron.style.transform = ""; chevron.style.color = ""; }
    };
  }, [playing, target, controls, step]);

  if (!playing) return null;
  const ref = (key) => (node) => { parts.current[key] = node; };
  return createPortal(<div className="collapsed-days-guide" aria-hidden="true" data-collapsed-days-guide="" data-guide-step={step}>
    <svg>
      <defs>
        <linearGradient ref={ref("gradient")} id="collapsed-days-guide-stroke" gradientUnits="userSpaceOnUse">
          <stop offset="0" className="collapsed-days-guide-stop-start" /><stop offset="1" className="collapsed-days-guide-stop-end" />
        </linearGradient>
      </defs>
      <g ref={ref("arrow")} className="collapsed-days-guide-arrow" style={{ opacity: 0 }}>
        <path ref={ref("halo")} className="collapsed-days-guide-halo" />
        <path ref={ref("trail")} className="collapsed-days-guide-trail" />
        <g ref={ref("head")}><path d="M 2 0 L -13 -7.5 Q -9 0 -13 7.5 Z" className="collapsed-days-guide-head" /></g>
      </g>
    </svg>
    <div ref={ref("bubble")} className="collapsed-days-guide-bubble" style={{ opacity: 0 }}>
      <span className="collapsed-days-guide-icon"><ChevronDown size={15} strokeWidth={2.6} /></span>
      <span><b>{steps[step].title}</b><small>{steps[step].text}</small></span>
    </div>
    {[0, 1].map((index) => <span key={index} ref={(node) => { (parts.current.rings ||= [])[index] = node; }} className="collapsed-days-guide-ring" style={{ opacity: 0 }} />)}
  </div>, document.body);
}
